import { useEffect, useState } from "react";

import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import Button from "@atlaskit/button";
import { useActivityClient, ActivityItem } from "@atlassian/recent-work-client";

import {
    AisSitesType,
    AvailableProductsResponse,
    useBxpWebStargateClient
} from "@/libraries/bxp-web-stargate-client/src/index";
import { Logo } from "@/libraries/wpl/foundations/src";

import { useAnalyticsClient } from "../../analytics/wac-analytics/analytics-client";
import { useLoggedInUser } from "../../utils";

import {
    TRACKING_EVENT_NAMES,
    loggedInHeroAnalyticsPayload,
    loggedInHeroLoadedAnalyticsPayload
} from "./analytics";
import ArrowCircle from "./assets/arrow-circle";
import Target from "./assets/target";

const GRAPHQL_GATEWAY_URL =
    process.env["NODE_ENV"] === "production"
        ? "https://www.atlassian.com/gateway/api/graphql"
        : "https://wac.stg.internal.atlassian.com/gateway/api/graphql";

const START_URL =
    process.env["NODE_ENV"] === "production"
        ? "https://start.atlassian.com"
        : "https://start.stg.atlassian.com";

interface HeroSectionProps {
    availableJiraSites: AisSitesType[];
    userProfileName: string | undefined;
}

export const filterAvailableJiraSites = (
    availableProducts: AvailableProductsResponse | null
): AisSitesType[] => {
    if (availableProducts) {
        const filteredSites =
            availableProducts.sites.filter((site) => {
                return site.products.includes("jira-software.ondemand");
            }) || [];

        return filteredSites;
    } else {
        return [];
    }
};

const constructLoginUrl = (url: string) => {
    const loginUrl = new URL(url);
    loginUrl.pathname = "/login.jsp";
    const searchParam = new URLSearchParams();
    const browseUrl = new URL(loginUrl);
    browseUrl.pathname = "/browse";
    searchParam.append("os_destination", browseUrl.href);
    loginUrl.search = searchParam.toString();
    return loginUrl;
};

const getSitenameFromUrl = (url: string) => {
    try {
        const tempUrl = new URL(url);
        return tempUrl.hostname.split(".")[0] || "";
    } catch {
        return "";
    }
};

const HeroSection = ({
    availableJiraSites,
    userProfileName
}: HeroSectionProps) => {
    const loggedInUserName = useLoggedInUser().name;
    // Splitting the name string by a space to separate possible first/last names
    const splitName = userProfileName
        ? userProfileName.split(" ")
        : loggedInUserName?.split(" ");

    const stargateClient = useBxpWebStargateClient();
    const activityClient = useActivityClient(
        "v3",
        "www.atlassian.com",
        undefined,
        GRAPHQL_GATEWAY_URL
    );

    // Default state to whatever we might have got from SSR
    const [hasMultipleJiras, setHasMulitpleJiras] = useState(
        availableJiraSites.length > 1
    );

    const [siteName, setSiteName] = useState(
        getSitenameFromUrl(availableJiraSites[0]?.url || "")
    );

    const [siteUrl, setSiteUrl] = useState("");

    useEffect(() => {
        const promises = [];

        // Fetch available-products if not available from SSR
        if (availableJiraSites.length == 0) {
            promises.push(
                stargateClient
                    .availableProducts({})
                    .then((data) => {
                        filterAvailableJiraSites(data);
                    })
                    .catch((error) => console.log(error))
            );
        }

        // Fetch recent Jira activity
        let mostRecentActivity: ActivityItem | undefined;
        const fetchActivitiesPromise = activityClient
            .fetchActivities(
                [50, 125, 250], // retries
                {
                    limit: 5,
                    products: ["jira"]
                },
                ["workedOn", "viewed"]
            )
            .then((data) => {
                // Which is more recent? The newest viewed or the newest worked on object?
                const mostRecentlyViewed = data.viewed[0];
                const mostRecentlyWorkedOn = data.workedOn[0];

                if (mostRecentlyViewed && mostRecentlyWorkedOn) {
                    mostRecentActivity =
                        new Date(mostRecentlyViewed.timestamp) >
                        new Date(mostRecentlyWorkedOn.timestamp)
                            ? mostRecentlyViewed
                            : mostRecentlyWorkedOn;
                } else if (mostRecentlyWorkedOn) {
                    mostRecentActivity = mostRecentlyWorkedOn;
                } else if (mostRecentlyViewed) {
                    mostRecentActivity = mostRecentlyViewed;
                }
            })
            .catch((error) => console.log(error));
        promises.push(fetchActivitiesPromise);

        // Set state after receiving all responses
        Promise.allSettled(promises).then(() => {
            // Set hasMultipleJiras based on availableJiraSites result
            setHasMulitpleJiras(availableJiraSites.length > 1);

            // First try to use most recent activity if available
            if (mostRecentActivity) {
                const loginUrl = constructLoginUrl(
                    mostRecentActivity.object.url
                );
                setSiteUrl(loginUrl.href);
                setSiteName(loginUrl.hostname.split(".")[0] || "");
            }

            // If no recent activity, use the first available jira site
            else if (availableJiraSites[0]) {
                const loginUrl = constructLoginUrl(availableJiraSites[0].url);
                setSiteUrl(loginUrl.href);
                setSiteName(loginUrl.hostname.split(".")[0] || "");
            }

            // If nothing available, use start
            else {
                setSiteUrl(START_URL);
                setSiteName("");
            }
        });
    }, [stargateClient, activityClient, availableJiraSites]);

    const { createAnalyticsEvent } = useAnalyticsEvents();
    const analyticsClient = useAnalyticsClient();

    useEffect(() => {
        //if client side, fire analytics event
        if (typeof window !== "undefined") {
            createAnalyticsEvent({
                ...loggedInHeroLoadedAnalyticsPayload
            }).fire();
        }
    }, [analyticsClient, createAnalyticsEvent]);

    const clickHandler = (eventName: TRACKING_EVENT_NAMES) => {
        createAnalyticsEvent({
            ...loggedInHeroAnalyticsPayload,
            attributes: {
                eventName,
                url: window.location.href,
                atlasId: "ATLAS-53087"
            }
        }).fire();
    };

    return (
        <section css={jswHeroSectionStyle}>
            <div css={targetStyle}>
                <Target />
            </div>
            <div css={arrowCircleStyle}>
                <ArrowCircle />
            </div>
            <div css={heroTextSectionStyle}>
                <h1 css={headingStyle}>
                    {splitName && splitName.length > 0 ? (
                        <FormattedMessage
                            id="JiraSoftwarePageTemplate.loggedInHero.welcomeBackWithName"
                            defaultMessage={`Welcome back, {name}.`}
                            values={{ name: splitName[0] }}
                        />
                    ) : (
                        <FormattedMessage
                            id="JiraSoftwarePageTemplate.loggedInHero.welcomeBack"
                            defaultMessage="Welcome back"
                        />
                    )}
                </h1>
                <p css={subHeadingStyle}>
                    <FormattedMessage
                        id="JiraSoftwarePageTemplate.loggedInHero.pickUpWhereYouLeftOff"
                        defaultMessage="Pick up where you left off in Jira Software."
                    />
                </p>
                <a
                    css={jiraSiteBoxStyle}
                    href={siteUrl}
                    onClick={() =>
                        clickHandler(
                            TRACKING_EVENT_NAMES.loggedInHeroProductCtaClicked
                        )
                    }
                >
                    <div css={logoAndSiteNameStyle}>
                        <div css={logoStyle}>
                            <Logo
                                brand="jirasoftware"
                                type="mark"
                                appearance="brand"
                            />
                        </div>
                        <div css={siteStyle}>
                            <div css={productStyle}>Jira Software</div>
                            <div css={siteNameStyle}>{siteName}</div>
                        </div>
                    </div>
                    <Button
                        id="jira-button"
                        appearance="warning"
                        css={buttonStyle}
                    >
                        <FormattedMessage
                            id="JiraSoftwarePageTemplate.loggedInHero.goToJira"
                            defaultMessage="Go to Jira"
                        />
                    </Button>
                </a>
                {hasMultipleJiras && (
                    <Button
                        href={START_URL}
                        appearance="subtle-link"
                        onClick={() =>
                            clickHandler(
                                TRACKING_EVENT_NAMES.loggedInHeroAnotherSiteCtaClicked
                            )
                        }
                    >
                        <span css={otherJiraSiteStyle}>
                            <FormattedMessage
                                id="JiraSoftwarePageTemplate.loggedInHero.lookingForAnotherSite"
                                defaultMessage="I’m looking for another Jira site"
                            />
                        </span>
                    </Button>
                )}
            </div>
        </section>
    );
};

const jswHeroSectionStyle = css({
    backgroundImage: "linear-gradient(180deg, #253858 0%, #091E42 100%)",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 15px"
});

const targetStyle = css({
    "position": "absolute",
    "insetInlineStart": "0",
    "insetBlockStart": "0",
    "@media (max-width: 1000px)": {
        display: "none"
    }
});

const arrowCircleStyle = css({
    "position": "absolute",
    "insetInlineEnd": "0",
    "insetBlockStart": "250px",
    "@media (max-width: 1000px)": {
        display: "none"
    }
});

const heroTextSectionStyle = css({
    "color": "white",
    "width": "100%",
    "maxWidth": "928px",
    "margin": "100px 170px 50px 170px",
    "p": {
        fontSize: "20px",
        lineHeight: "28px",
        fontWeight: "400"
    },
    "@media (max-width: 1000px)": {
        margin: "100px 15px 50px 15px"
    }
});

const jiraSiteBoxStyle = css({
    width: "100%",
    height: "72px",
    display: "flex",
    justifyContent: "space-between",
    flexDirection: "row",
    alignItems: "center",
    border: "2px solid #579DFF",
    padding: "10px 10px",
    marginBlockStart: "20px",
    marginBlockEnd: "20px"
});

const headingStyle = css({
    fontSize: "48px",
    lineHeight: "56px",
    fontWeight: "500",
    marginBlockEnd: "15px"
});

const siteStyle = css({
    lineHeight: "22px",
    fontSize: "16px",
    overflow: "hidden"
});

const productStyle = css({
    fontWeight: "500",
    height: "22px",
    fontSize: "17px"
});

const siteNameStyle = css({
    fontWeight: "300",
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
    height: "22px"
});

const buttonStyle = css({
    marginInlineStart: "15px"
});

const otherJiraSiteStyle = css({
    marginBlockStart: "15px",
    fontSize: "16px",
    textDecoration: "underline",
    color: "white",
    fontWeight: "400px"
});

const subHeadingStyle = css({
    marginBlockStart: "15px",
    marginBlockEnd: "15px"
});

const logoStyle = css({
    marginInlineEnd: "10px",
    marginBlockStart: "9px"
});

const logoAndSiteNameStyle = css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    minWidth: "0px"
});

export default HeroSection;
